"use client";
import { useLocale } from "next-intl";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { memo, useMemo, useState, useEffect } from "react";
import { SingleValue } from "react-select";
import { SharedHeroBannerDatasource, JapanHotel } from "@/types/generated";
import { cityAndHotelFuzzyFilter, groupJapanAreas } from "@/utils";
import {
  FilterAvailableRoom,
  SearchAvailableRoom,
} from "../search-avaliable/FilterAvailableRoom";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import { getSearchRoomData } from "../hotel-result/filter/utils";
import { useParams } from "next/navigation";
import { RegionOption } from "./DestinationBanner";
import { DEFAULT_CHECKIN_DATE, DEFAULT_CHECKOUT_DATE } from "@/constants";
import { CustomSelect } from "@/components/atoms/dropdown";
import { Button } from "@/components/atoms/button";
/**
 * RegionSelection Component
 * Displays a dropdown to select regions and a booking filter modal.
 * - Dynamically maps and filters Japan area data.
 * - Pre-fills region and search form state based on the current destination.
 * - Uses requestAnimationFrame within useEffect for smooth, non-blocking updates.
 */
const RegionSelection = memo(
  ({
    regionOptions,
    onRegionSelect,
    bannerDatasource,
    destinationTitle,
  }: {
    regionOptions: RegionOption[];
    onRegionSelect: (region: RegionOption) => void;
    bannerDatasource?: SharedHeroBannerDatasource;
    destinationTitle: string;
  }) => {
    const locale = useLocale();
    const [filterState, setFilterState] = useState<SearchAvailableRoom | null>(
      null,
    );
    const onFilterChange = (f: SearchAvailableRoom | null) => setFilterState(f);

    const { japanAreas, allLocaleHotels } = useJapanAreasStore();

    // Groups Japan areas and their associated hotels for dropdown display
    const listHotelAndCityData = useMemo(() => {
      if (!japanAreas?.length) return [];
      return groupJapanAreas(
        japanAreas,
        allLocaleHotels as Record<string, JapanHotel[]> | null | undefined,
        undefined,
        "都市",
        "ホテル",
      );
    }, [japanAreas, allLocaleHotels]);

    const params = useParams();
    const { configurations: searchAvailableRoom } = useSearchFormStore();
    const searchRoomData = getSearchRoomData(searchAvailableRoom, params);

    // Extracted room configuration values for booking modal
    const {
      configurations,
      rooms_configurations,
      booking,
      ages_configurations,
      tripla_section,
    } = searchRoomData || {};

    const {
      checkin_label,
      checkout_label,
      promo_code_label,
      promo_code_placeholder,
    } = configurations ?? {};

    const {
      maximum_adults,
      maximum_child_age,
      maximum_children,
      maximum_rooms,
      default_adult_count,
    } = rooms_configurations ?? {};

    const {
      tripla_add_room_label,
      tripla_bed_label,
      tripla_room_adults_label,
      tripla_room_children_label,
      tripla_room_name_label,
      tripla_with_bed_label,
      tripla_without_bed_label,
    } = tripla_section ?? {};

    const { booking_button_text } = booking ?? {};
    const { ages_label, age_placeholder, single_child_label } =
      ages_configurations ?? {};

    // Finds the prefecture that matches the current destination title
    const findMatchPrefecture = useMemo(() => {
      return japanAreas.find(
        (area) =>
          area.title === destinationTitle ||
          area.item_name === destinationTitle ||
          area.area_name === destinationTitle,
      );
    }, [japanAreas, destinationTitle]);

    const { formData } = useSearchFormStore();

    // Sets default filter state when the prefecture matches
    // Uses requestAnimationFrame to avoid synchronous re-render issues
    useEffect(() => {
      if (findMatchPrefecture) {
        const frameId = requestAnimationFrame(() => {
          const matchedRegion = listHotelAndCityData
            .flatMap((item) => item.options)
            .find((option) => option.value === findMatchPrefecture.documentId);
          if (matchedRegion) {
            onFilterChange({
              hotelOrCity: matchedRegion,
              checkIn: new Date(formData?.checkIn || DEFAULT_CHECKIN_DATE),
              checkout: new Date(formData?.checkout || DEFAULT_CHECKOUT_DATE),
            });
          }
        });

        return () => cancelAnimationFrame(frameId); // Cleanup
      }
    }, [findMatchPrefecture, listHotelAndCityData, formData]); // Added correct deps safely

    const [selectedRegion, setSelectedRegion] = useState<RegionOption | null>(
      null,
    );

    //  useEffect + requestAnimationFrame for second setState
    useEffect(() => {
      if (regionOptions.length > 0 && findMatchPrefecture) {
        const frameId = requestAnimationFrame(() => {
          const matched = regionOptions.find(
            (option) => option.value === findMatchPrefecture.documentId,
          );
          setSelectedRegion(matched || null);
        });

        return () => cancelAnimationFrame(frameId); // Cleanup
      }
    }, [regionOptions, findMatchPrefecture]);

    return (
      <div className="pointer-events-auto mt-2 flex justify-between gap-2 md:justify-center md:gap-4">
        <div className="max-w-[200px] min-w-0 flex-1">
          <CustomSelect
            options={regionOptions}
            value={selectedRegion}
            placeholder={
              bannerDatasource?.labels?.view_other_regions || "他の地域を見る"
            }
            className="w-full"
            size="md"
            onChange={(newValue) => {
              const singleValue = newValue as SingleValue<RegionOption>;
              if (singleValue) {
                onRegionSelect(singleValue);
              }
            }}
            filterOption={(
              option: { label: string; value: string; data: RegionOption },
              currentInputValue: string,
            ) => {
              const isMatch = cityAndHotelFuzzyFilter(
                {
                  label: option.data.label,
                  data: { searchNames: option.data.searchNames },
                },
                currentInputValue,
              );
              return isMatch;
            }}
            styles={{
              control: (provided: object) => ({
                ...provided,
                minHeight: "43px",
                height: "43px",
                borderColor: "var(--color-primary-950)",
                paddingLeft: "12px",
                paddingRight: "12px",
                cursor: "pointer",
              }),
              valueContainer: (provided: object) => ({
                ...provided,
                height: "43px",
              }),
              placeholder: (provided: object) => ({
                ...provided,
                fontSize: "13px",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "var(--color-primary-950)",
              }),
              singleValue: (provided: object) => ({
                ...provided,
                fontSize: "13px",
                fontWeight: 500,
              }),
            }}
          />
        </div>

        <FilterAvailableRoom
          locale={locale}
          className=""
          trigger={
            <Button
              variant="secondary"
              size="xl"
              type="button"
              className="flex h-[43px] items-center justify-between px-6 text-[13px] text-[var(--color-primary-950)]"
            >
              <span>{bannerDatasource?.labels?.book_my_hotel}</span>
            </Button>
          }
          listOption={listHotelAndCityData}
          configurations={{
            checkinLabel: checkin_label ?? "",
            checkoutLabel: checkout_label ?? "",
            promoCodeLabel: promo_code_label ?? "",
            promoCodePlaceholder: promo_code_placeholder ?? "",
          }}
          roomsConfigurations={{
            adultLabel: tripla_room_adults_label ?? "",
            childrenLabel: tripla_room_children_label ?? "",
            addRoomButtonText: tripla_add_room_label ?? "",
            maximumAdults: maximum_adults ?? "",
            maximumChildAge: maximum_child_age ?? "",
            maximumChildren: maximum_children ?? "",
            maximumRooms: maximum_rooms ?? "",
            singleRoomLabel: tripla_room_name_label ?? "",
            bedLabel: tripla_bed_label ?? "",
            withBedLabel: tripla_with_bed_label ?? "",
            withoutBedLabel: tripla_without_bed_label ?? "",
            default_adult_count: default_adult_count ?? "",
          }}
          booking={{
            bookingButtonText: booking_button_text ?? "",
          }}
          agesConfigurations={{
            agesLabel: ages_label ?? "",
            agePlaceholder: age_placeholder ?? "",
            singleChildLabel: single_child_label ?? "",
          }}
          onFilterChange={onFilterChange}
          filterState={filterState}
          isFilterOnDestionationPage={true}
        />
      </div>
    );
  },
);

RegionSelection.displayName = "RegionSelection";
export default RegionSelection;
